import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { generateCoachingReport } from '../data/leaderCoachLogic';

export const LeaderCoach = () => {
  const [metrics, setMetrics] = useState({
    leadership: 48,
    psychSafety: 42,
    stress: 72,
    conflict: 55
  });
  const [report, setReport] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const startCoaching = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setReport(generateCoachingReport(metrics));
      setIsProcessing(false);
    }, 2000);
  };

  return (
    <div className="space-y-6 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Leader Coach</h1>
          <p className="text-slate-500 mt-1">Personlig veiledning basert på teamets psykososiale puls.</p>
        </div>
        {report && (
          <Button variant="secondary" onClick={() => setReport(null)} icon={<SafeIcon name="RefreshCw" />}>
            Start Ny Sesjon
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!report ? (
          <motion.div key="input" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-6">
            <Card>
              <CardHeader title="Dine Leder-data" subtitle="Juster verdiene basert på siste kartlegging" icon={<SafeIcon name="Sliders" />} />
              <CardContent className="space-y-8">
                {Object.entries(metrics).map(([key, value]) => (
                  <div key={key} className="space-y-3">
                    <div className="flex justify-between">
                      <label className="text-sm font-bold text-slate-700 capitalize">
                        {key === 'leadership' ? 'Leder-score' : key === 'psychSafety' ? 'Psykologisk Trygghet' : key === 'stress' ? 'Stressnivå' : 'Konfliktnivå'}
                      </label>
                      <span className="text-sm font-black text-indigo-600">{value}%</span>
                    </div>
                    <input 
                      type="range" min="0" max="100" value={value}
                      onChange={(e) => setMetrics({...metrics, [key]: parseInt(e.target.value)})}
                      className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                  </div>
                ))}
                <div className="pt-4">
                  <Button 
                    variant="primary" className="w-full py-4 text-lg" 
                    onClick={startCoaching} disabled={isProcessing}
                    icon={<SafeIcon name="Cpu" />}
                  >
                    {isProcessing ? 'AI analyserer lederstil...' : 'Hent Coaching-råd'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div key="report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Executive Summary */}
            <Card className="bg-indigo-900 border-none text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
              <CardContent className="p-10 flex gap-8 items-center relative z-10">
                <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center shrink-0 backdrop-blur-md">
                  <SafeIcon name="Award" className="text-4xl text-indigo-300" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">Lederinnsikt</h2>
                  <p className="text-indigo-100 text-lg italic leading-relaxed">"{report.summary}"</p>
                </div>
              </CardContent>
            </Card>

            {/* Coaching Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {report.coachingAreas.map((area) => (
                <Card key={area.id} className="h-full border-none shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader 
                    title={area.title} 
                    icon={<div className={`p-2 bg-${area.color}-50 text-${area.color}-600 rounded-lg`}><SafeIcon name={area.icon} /></div>} 
                  />
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-700 leading-relaxed font-medium">
                      {area.advice}
                    </p>
                    <div className="space-y-2 pt-2">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Konkrete Tips</p>
                      {area.tips.map((tip, i) => (
                        <div key={i} className="flex gap-2 text-xs text-slate-500 items-start">
                          <SafeIcon name="Check" className={`text-${area.color}-500 shrink-0 mt-0.5`} />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Improvement Plan */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader title="Leadership Improvement Plan" subtitle="Dine neste 3 steg for suksess" icon={<SafeIcon name="Target" />} />
                  <CardContent className="space-y-6">
                    {report.improvementPlan.map((step) => (
                      <div key={step.step} className="flex gap-6 items-start p-4 bg-slate-50 rounded-2xl border border-slate-100 group hover:bg-white hover:shadow-md transition-all">
                        <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold shrink-0">
                          {step.step}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{step.title}</h4>
                          <p className="text-sm text-slate-500 mt-1">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="bg-slate-900 border-none text-white">
                  <CardContent className="p-8 space-y-6">
                    <SafeIcon name="FileText" className="text-4xl text-indigo-400" />
                    <h3 className="text-xl font-bold">Personlig Rapport</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Last ned din fullstendige coaching-rapport som PDF, inkludert samtale-manus og øvelser.
                    </p>
                    <Button variant="secondary" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                      Last ned PDF
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-emerald-50 border-emerald-100">
                  <CardContent className="p-4 flex gap-3">
                    <SafeIcon name="Zap" className="text-emerald-600 mt-1" />
                    <div>
                      <p className="text-xs font-bold text-emerald-900">AI Coaching Tips</p>
                      <p className="text-[10px] text-emerald-700 leading-relaxed">
                        Ledere som følger disse rådene ser i snitt en 12% forbedring i teamets trygghetsscore over 3 måneder.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};